import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell,
} from "recharts";
import {
  getAnalyticsOverview,
  getAnalyticsChannels,
  getAnalyticsRealtime,
  getAnalyticsDevicesGeo,
  type KpiDelta,
} from "@/lib/analytics/admin.functions";
import { listAdminLeads } from "@/lib/admin-leads.functions";
import { listEmailLog } from "@/lib/admin-emails.functions";

export const Route = createFileRoute("/_authenticated/admin/")({
  head: () => ({
    meta: [
      { title: "Dashboard — Fenny admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminDashboard,
});

type RangeKey = 7 | 14 | 30 | 90;

function fmtNum(n: number | undefined | null) {
  if (n === undefined || n === null || Number.isNaN(n)) return "—";
  if (n >= 1000) return n.toLocaleString("sv-SE");
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(1);
}
function fmtPct(v: number | undefined | null, digits = 1) {
  if (v === undefined || v === null || Number.isNaN(v)) return "—";
  return `${(v * 100).toFixed(digits)}%`;
}
function fmtSec(s: number | undefined | null) {
  if (!s && s !== 0) return "—";
  if (s < 60) return `${s.toFixed(0)}s`;
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  return `${m}m ${r}s`;
}
function deltaBadge(d: KpiDelta | undefined, invert = false) {
  if (!d || d.delta_pct === null) return <span className="text-xs text-white/40">—</span>;
  const positive = invert ? d.delta_pct < 0 : d.delta_pct > 0;
  const neutral = Math.abs(d.delta_pct) < 0.5;
  const color = neutral ? "text-white/40" : positive ? "text-emerald-400" : "text-rose-400";
  const sign = d.delta_pct > 0 ? "+" : "";
  return <span className={`text-xs font-medium ${color}`}>{sign}{d.delta_pct.toFixed(1)}%</span>;
}

function Kpi({
  label, value, sub, delta, invertDelta,
}: { label: string; value: string | number; sub?: string; delta?: KpiDelta; invertDelta?: boolean }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs uppercase tracking-wider text-white/50">{label}</div>
        {delta && deltaBadge(delta, invertDelta)}
      </div>
      <div className="mt-2 text-3xl font-semibold text-white">{value}</div>
      {sub && <div className="mt-1 text-xs text-white/40">{sub}</div>}
    </div>
  );
}

function Card({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-white/10 bg-white/[0.02]">
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
        <h2 className="text-sm font-semibold">{title}</h2>
        {action}
      </div>
      <div>{children}</div>
    </section>
  );
}

const PIE_COLORS = ["#7CFFB2", "#5BA7FF", "#F5A524", "#F471B5", "#A78BFA", "#34D399"];

function AdminDashboard() {
  const [days, setDays] = useState<RangeKey>(7);

  const overviewFn = useServerFn(getAnalyticsOverview);
  const channelsFn = useServerFn(getAnalyticsChannels);
  const realtimeFn = useServerFn(getAnalyticsRealtime);
  const devicesFn = useServerFn(getAnalyticsDevicesGeo);
  const leadsFn = useServerFn(listAdminLeads);
  const emailsFn = useServerFn(listEmailLog);

  const overview = useQuery({
    queryKey: ["admin", "dashboard", "overview", days],
    queryFn: () => overviewFn({ data: { days } }),
  });
  const channels = useQuery({
    queryKey: ["admin", "dashboard", "channels", days],
    queryFn: () => channelsFn({ data: { days } }),
  });
  const devices = useQuery({
    queryKey: ["admin", "dashboard", "devices", days],
    queryFn: () => devicesFn({ data: { days } }),
  });
  const realtime = useQuery({
    queryKey: ["admin", "dashboard", "realtime"],
    queryFn: () => realtimeFn(),
    refetchInterval: 15000,
  });
  const leads = useQuery({
    queryKey: ["admin", "dashboard", "leads"],
    queryFn: () => leadsFn(),
    refetchInterval: 60000,
  });
  const emails = useQuery({
    queryKey: ["admin", "dashboard", "emails"],
    queryFn: () => emailsFn(),
    refetchInterval: 60000,
  });

  const recentLeads = (leads.data?.leads ?? []).slice(0, 8);

  const rangeMs = days * 86400000;
  const leadsInRange = (leads.data?.leads ?? []).filter(
    (l) => Date.now() - new Date(l.created_at).getTime() < rangeMs,
  );
  const emailRowsInRange = (emails.data?.rows ?? []).filter(
    (e) => Date.now() - new Date(e.sent_at).getTime() < rangeMs,
  );
  const emailsSent = emailRowsInRange.filter((e) => e.status === "sent").length;
  const emailsFailed = emailRowsInRange.filter((e) => e.status !== "sent").length;
  const emailSuccess = emailRowsInRange.length
    ? emailsSent / emailRowsInRange.length
    : null;

  const leadsBySource = useMemo(() => {
    const m = new Map<string, number>();
    for (const l of leadsInRange) m.set(l.source_label, (m.get(l.source_label) ?? 0) + 1);
    return [...m.entries()].map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [leadsInRange]);

  const pipelineValue = leadsInRange.reduce((a, l) => a + (l.value ?? 0), 0);

  const daily = overview.data?.daily ?? [];
  const chartData = daily.map((d) => ({
    date: d.date.slice(5),
    Sessioner: d.sessions,
    Besökare: d.visitors,
    Leads: d.leads,
  }));

  const topChannels = (channels.data ?? []).slice(0, 6);
  const topPages = overview.data?.top_pages ?? [];
  const topReferrers = overview.data?.top_referrers ?? [];

  return (
    <div className="p-6 md:p-8 space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
          <p className="text-sm text-white/50">Översikt över trafik, leads och kommunikation</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-white/10 bg-white/[0.02] p-1">
            {([7, 14, 30, 90] as RangeKey[]).map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-3 py-1 text-xs rounded-md transition ${
                  days === d ? "bg-white/10 text-white" : "text-white/50 hover:text-white"
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
          {realtime.data && (
            <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
              </span>
              <span className="text-xs text-white/70">
                {realtime.data.active_visitors} live · {realtime.data.active_sessions} sessioner
              </span>
            </div>
          )}
        </div>
      </header>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi
          label="Besökare"
          value={fmtNum(overview.data?.visitors.value)}
          delta={overview.data?.visitors}
          sub={`vs föregående ${days}d`}
        />
        <Kpi
          label="Sessioner"
          value={fmtNum(overview.data?.sessions.value)}
          delta={overview.data?.sessions}
          sub={`${fmtNum(overview.data?.pageviews.value)} sidvisningar`}
        />
        <Kpi
          label="Leads"
          value={fmtNum(overview.data?.leads.value)}
          delta={overview.data?.leads}
          sub={overview.data ? `${fmtPct(overview.data.conv_rate.value)} konv.` : undefined}
        />
        <Kpi
          label="Pipeline-värde"
          value={pipelineValue ? `${pipelineValue.toLocaleString("sv-SE")} kr` : "—"}
          sub={`${leadsInRange.length} leads i perioden`}
        />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi
          label="Avhopp"
          value={fmtPct(overview.data?.bounce_rate.value)}
          delta={overview.data?.bounce_rate}
          invertDelta
        />
        <Kpi
          label="Engagerade"
          value={fmtPct(overview.data?.engaged_rate.value)}
          delta={overview.data?.engaged_rate}
        />
        <Kpi
          label="Snitt sessionstid"
          value={fmtSec(overview.data?.avg_session_sec.value)}
          delta={overview.data?.avg_session_sec}
        />
        <Kpi
          label="Mail skickade"
          value={fmtNum(emailRowsInRange.length)}
          sub={
            emailSuccess !== null
              ? `${fmtPct(emailSuccess, 0)} levererat · ${emailsFailed} fel`
              : "Ingen data"
          }
        />
      </div>

      {/* Trend chart */}
      <Card
        title={`Trafik & leads — senaste ${days} dagarna`}
        action={
          <Link to="/admin/analytics" className="text-xs text-mint hover:underline">
            Öppna analytics →
          </Link>
        }
      >
        <div className="h-72 px-2 py-4">
          {chartData.length === 0 ? (
            <div className="h-full grid place-items-center text-sm text-white/40">Ingen data ännu.</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="gSess" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#5BA7FF" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#5BA7FF" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gVis" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#7CFFB2" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#7CFFB2" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gLeads" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#F5A524" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="#F5A524" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="date" stroke="rgba(255,255,255,0.4)" fontSize={11} />
                <YAxis stroke="rgba(255,255,255,0.4)" fontSize={11} />
                <Tooltip
                  contentStyle={{
                    background: "#0b0b0f", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12,
                  }}
                />
                <Area type="monotone" dataKey="Sessioner" stroke="#5BA7FF" fill="url(#gSess)" strokeWidth={2} />
                <Area type="monotone" dataKey="Besökare" stroke="#7CFFB2" fill="url(#gVis)" strokeWidth={2} />
                <Area type="monotone" dataKey="Leads" stroke="#F5A524" fill="url(#gLeads)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </Card>

      {/* Two-up: leads by source + top channels */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Leads per källa" action={<Link to="/admin/leads" className="text-xs text-mint hover:underline">Alla leads →</Link>}>
          <div className="p-5">
            {leadsBySource.length === 0 ? (
              <div className="text-sm text-white/40">Inga leads i perioden.</div>
            ) : (
              <div className="grid grid-cols-2 gap-4 items-center">
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={leadsBySource} dataKey="value" nameKey="name" innerRadius={40} outerRadius={70} paddingAngle={2}>
                        {leadsBySource.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{ background: "#0b0b0f", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <ul className="space-y-2 text-sm">
                  {leadsBySource.map((s, i) => (
                    <li key={s.name} className="flex items-center justify-between gap-3">
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="h-2 w-2 rounded-full shrink-0" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                        <span className="truncate text-white/80">{s.name}</span>
                      </span>
                      <span className="text-white/60">{s.value}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Card>

        <Card title="Topp-kanaler" action={<Link to="/admin/analytics" className="text-xs text-mint hover:underline">Detaljer →</Link>}>
          <div className="divide-y divide-white/5">
            {channels.isLoading && <div className="p-5 text-sm text-white/40">Laddar…</div>}
            {!channels.isLoading && topChannels.length === 0 && (
              <div className="p-5 text-sm text-white/40">Ingen kanaldata.</div>
            )}
            {topChannels.map((c, i) => (
              <div key={i} className="px-5 py-3 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-sm text-white truncate">{c.source} <span className="text-white/40">/ {c.medium}</span></div>
                  <div className="text-xs text-white/50">{c.group}{c.campaign ? ` · ${c.campaign}` : ""}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm text-white">{c.sessions} sess.</div>
                  <div className="text-xs text-white/50">{c.leads} leads · {fmtPct(c.conv_rate)}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Three-up: top pages, referrers, devices */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card title="Mest besökta sidor">
          <div className="divide-y divide-white/5">
            {topPages.length === 0 && <div className="p-5 text-sm text-white/40">Ingen data.</div>}
            {topPages.slice(0, 8).map((p) => (
              <div key={p.path} className="px-5 py-2.5 flex items-center justify-between gap-3">
                <span className="text-sm text-white/80 truncate">{p.path}</span>
                <span className="text-xs text-white/50 shrink-0">{p.views}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Toppreferenser">
          <div className="divide-y divide-white/5">
            {topReferrers.length === 0 && <div className="p-5 text-sm text-white/40">Inga referrers.</div>}
            {topReferrers.slice(0, 8).map((r) => (
              <div key={r.host} className="px-5 py-2.5 flex items-center justify-between gap-3">
                <span className="text-sm text-white/80 truncate">{r.host}</span>
                <span className="text-xs text-white/50 shrink-0">{r.sessions}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Enheter">
          <div className="p-5">
            {devices.data?.devices?.length ? (
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={devices.data.devices.slice(0, 6)}>
                    <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="name" stroke="rgba(255,255,255,0.4)" fontSize={11} />
                    <YAxis stroke="rgba(255,255,255,0.4)" fontSize={11} />
                    <Tooltip
                      contentStyle={{ background: "#0b0b0f", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }}
                    />
                    <Bar dataKey="count" fill="#7CFFB2" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="text-sm text-white/40">Ingen data.</div>
            )}
          </div>
        </Card>
      </div>

      {/* Recent leads + live activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card
            title="Senaste leads"
            action={<Link to="/admin/leads" className="text-xs text-mint hover:underline">Visa alla →</Link>}
          >
            <div className="divide-y divide-white/5">
              {leads.isLoading && <div className="p-5 text-sm text-white/50">Laddar…</div>}
              {recentLeads.length === 0 && !leads.isLoading && (
                <div className="p-5 text-sm text-white/50">Inga leads än.</div>
              )}
              {recentLeads.map((l) => (
                <div key={l.id} className="px-5 py-3 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-sm text-white truncate">
                      {l.name ? `${l.name} · ` : ""}{l.email}
                    </div>
                    <div className="text-xs text-white/50 truncate">{l.summary}</div>
                  </div>
                  <div className="text-xs text-white/40 shrink-0 text-right">
                    <div className="text-white/60">{l.source_label}{l.value ? ` · ${l.value.toLocaleString("sv-SE")} kr` : ""}</div>
                    <div>{new Date(l.created_at).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" })}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <Card
          title="Live just nu"
          action={<Link to="/admin/analytics" className="text-xs text-mint hover:underline">Realtid →</Link>}
        >
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg bg-white/[0.03] p-3">
                <div className="text-xs text-white/50">Besökare</div>
                <div className="text-2xl font-semibold">{realtime.data?.active_visitors ?? "—"}</div>
              </div>
              <div className="rounded-lg bg-white/[0.03] p-3">
                <div className="text-xs text-white/50">Sessioner</div>
                <div className="text-2xl font-semibold">{realtime.data?.active_sessions ?? "—"}</div>
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-white/40 mb-2">Aktiva sidor</div>
              {realtime.data?.active_paths?.length ? (
                <ul className="space-y-1.5">
                  {realtime.data.active_paths.slice(0, 6).map((p) => (
                    <li key={p.path} className="flex items-center justify-between text-sm">
                      <span className="text-white/80 truncate">{p.path}</span>
                      <span className="text-white/50 text-xs">{p.count}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-sm text-white/40">Inga aktiva sidor.</div>
              )}
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-white/40 mb-2">Senaste händelser</div>
              {realtime.data?.recent_events?.length ? (
                <ul className="space-y-1.5">
                  {realtime.data.recent_events.slice(0, 5).map((e, i) => (
                    <li key={i} className="text-xs text-white/70 flex justify-between gap-2">
                      <span className="truncate">{e.name}{e.label ? ` · ${e.label}` : ""}</span>
                      <span className="text-white/40 shrink-0">{new Date(e.ts).toLocaleTimeString("sv-SE")}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-sm text-white/40">Inga händelser.</div>
              )}
            </div>
          </div>
        </Card>
      </div>

      {/* Quick links */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { to: "/admin/leads", label: "Leads", desc: "Hantera inkomna leads" },
          { to: "/admin/emails", label: "E-postlogg", desc: "Status & leverans" },
          { to: "/admin/blog", label: "Blogg", desc: "Skapa & redigera" },
          { to: "/admin/cases", label: "Cases", desc: "Portfolio & resultat" },
        ].map((q) => (
          <Link
            key={q.to}
            to={q.to}
            className="rounded-xl border border-white/10 bg-white/[0.02] p-4 hover:bg-white/[0.05] transition"
          >
            <div className="text-sm font-medium text-white">{q.label}</div>
            <div className="text-xs text-white/50 mt-1">{q.desc}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}
